import React, { useState } from 'react';
import {
  Loader, Check, Shield, ShieldAlert, ChevronDown, ChevronRight,
  Plus, ExternalLink, Trash2, Database, AlertTriangle, Link
} from 'lucide-react';
import { RuleModal, SourceModal } from './AdminModals';

const ProgramRules = ({ program, details, onAddRule, onDeleteRule }) => (
  <div style={{ marginBottom: '1.25rem' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
      <h4 style={{ fontSize: '0.9375rem', margin: 0 }}>Rules ({details?.rules?.length || 0})</h4>
      <button
        onClick={(e) => { e.stopPropagation(); onAddRule(program.program_id); }}
        className="btn btn-secondary btn-sm"
        style={{ padding: '0.375rem 0.75rem' }}
        data-testid={`add-rule-btn-${program.program_id}`}
      >
        <Plus size={14} /> Add Rule
      </button>
    </div>

    {details?.rules?.length > 0 ? (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {details.rules.map(rule => (
          <div key={rule.rule_id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '0.875rem', background: 'var(--bg-subtle)', borderRadius: 'var(--radius-md)', fontSize: '0.8125rem' }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.375rem', flexWrap: 'wrap' }}>
                <code style={{ background: 'rgba(246, 116, 37, 0.1)', color: 'var(--brand-purple)', padding: '2px 8px', borderRadius: '2px', fontSize: '0.75rem' }}>{rule.requirement_key}</code>
                <span style={{ color: 'var(--text-muted)' }}>{rule.operator}</span>
                <strong>{JSON.stringify(rule.requirement_value)}</strong>
                {rule.is_hard_gate && <span className="badge badge-error" style={{ fontSize: '0.625rem', padding: '2px 6px' }}>HARD GATE</span>}
                <span className={`badge ${rule.confidence === 'HIGH' ? 'badge-success' : 'badge-warning'}`} style={{ fontSize: '0.625rem', padding: '2px 6px' }}>{rule.confidence}</span>
              </div>
              {rule.notes && <div style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginBottom: '0.25rem' }}>{rule.notes}</div>}
              <a href={rule.source_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--brand-purple)', fontSize: '0.75rem', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                <ExternalLink size={12} /> {rule.source_title}
              </a>
            </div>
            <button onClick={() => onDeleteRule(rule.rule_id, program.program_id)} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px', color: 'var(--error)' }} data-testid={`delete-rule-${rule.rule_id}`}>
              <Trash2 size={16} />
            </button>
          </div>
        ))}
      </div>
    ) : (
      <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.875rem', background: 'var(--bg-subtle)', borderRadius: 'var(--radius-md)' }}>
        <AlertTriangle size={18} style={{ marginBottom: '0.5rem' }} />
        <br />No rules defined. Add official rules to enable scoring.
      </div>
    )}
  </div>
);

const ProgramSources = ({ program, details, onAddSource }) => (
  <div style={{ marginBottom: '1.25rem' }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
      <h4 style={{ fontSize: '0.9375rem', margin: 0 }}>Sources ({details?.sources?.length || 0})</h4>
      <button
        onClick={(e) => { e.stopPropagation(); onAddSource(program.program_id); }}
        className="btn btn-secondary btn-sm"
        style={{ padding: '0.375rem 0.75rem' }}
        data-testid={`add-source-btn-${program.program_id}`}
      >
        <Link size={14} /> Add Source
      </button>
    </div>
    {details?.sources?.length > 0 ? (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
        {details.sources.map(src => (
          <div key={src.source_id} style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', padding: '0.75rem', background: 'var(--bg-subtle)', borderRadius: 'var(--radius-md)', fontSize: '0.8125rem' }}>
            {src.is_official ? <Shield size={16} color="#10B981" /> : <ShieldAlert size={16} color="#D97706" />}
            <a href={src.source_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--brand-purple)', flex: 1 }}>{src.source_title}</a>
            <span style={{ fontSize: '0.6875rem', color: 'var(--text-muted)', background: 'var(--bg-elevated)', padding: '2px 8px', borderRadius: '2px' }}>{src.source_type}</span>
          </div>
        ))}
      </div>
    ) : (
      <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.875rem', background: 'var(--bg-subtle)', borderRadius: 'var(--radius-md)' }}>No sources added</div>
    )}
  </div>
);

const ProgramCard = ({ program, expanded, details, onToggle, onVerify, actionLoading, onAddRule, onAddSource, onDeleteRule }) => (
  <div className="card" style={{ padding: 0, overflow: 'hidden' }} data-testid={`program-${program.program_id}`}>
    <div
      onClick={() => onToggle(program.program_id)}
      style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '1rem 1.25rem', cursor: 'pointer',
        borderLeft: `4px solid ${program.status === 'VERIFIED' ? '#10B981' : '#D97706'}`
      }}
    >
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.25rem' }}>
          <span style={{ fontWeight: 600, fontSize: '1rem' }}>{program.country}</span>
          <span className={`badge ${program.status === 'VERIFIED' ? 'badge-success' : 'badge-warning'}`} style={{ fontSize: '0.625rem' }}>
            {program.status}
          </span>
        </div>
        <div style={{ fontSize: '0.875rem', color: 'var(--text-muted)' }}>{program.program_name}</div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
        <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', background: 'var(--bg-subtle)', padding: '4px 10px', borderRadius: 'var(--radius-sm)' }}>{program.track}</span>
        {expanded ? <ChevronDown size={18} color="var(--text-muted)" /> : <ChevronRight size={18} color="var(--text-muted)" />}
      </div>
    </div>

    {expanded && (
      <div style={{ padding: '0 1.25rem 1.25rem', borderTop: '1px solid var(--border-default)' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '1rem', padding: '1.25rem 0', fontSize: '0.875rem' }}>
          <div><span style={{ color: 'var(--text-muted)' }}>Work Allowed:</span> <strong>{program.work_allowed}</strong></div>
          <div><span style={{ color: 'var(--text-muted)' }}>PR Pathway:</span> <strong>{program.pr_pathway}</strong></div>
          <div><span style={{ color: 'var(--text-muted)' }}>Track:</span> <strong>{program.track}</strong></div>
        </div>
        {program.notes_public && <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', margin: '0 0 1rem', padding: '1rem', background: 'var(--bg-subtle)', borderRadius: 'var(--radius-md)' }}>{program.notes_public}</p>}

        <ProgramRules program={program} details={details} onAddRule={onAddRule} onDeleteRule={onDeleteRule} />
        <ProgramSources program={program} details={details} onAddSource={onAddSource} />

        {program.status === 'UNVERIFIED' && (
          <button
            onClick={() => onVerify(program.program_id)}
            disabled={actionLoading}
            className="btn btn-primary btn-sm"
            data-testid={`verify-btn-${program.program_id}`}
          >
            {actionLoading ? <Loader size={16} className="animate-spin" /> : <Check size={16} />}
            Promote to VERIFIED
          </button>
        )}
      </div>
    )}
  </div>
);

export const ProgramsTab = ({ programs, programStats, expandedProgram, programDetails, actionLoading,
  toggleProgram, verifyProgram, addRule, addSource, deleteRule, seedPrograms }) => {
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAddRule, setShowAddRule] = useState(null);
  const [showAddSource, setShowAddSource] = useState(null);

  const filteredPrograms = statusFilter === 'all'
    ? programs
    : programs.filter(p => p.status === statusFilter.toUpperCase());

  return (
    <div data-testid="programs-tab">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          {['all', 'verified', 'unverified'].map(f => (
            <button
              key={f}
              onClick={() => setStatusFilter(f)}
              data-testid={`filter-${f}`}
              style={{
                padding: '0.5rem 1rem',
                borderRadius: 'var(--radius-full)',
                border: statusFilter === f ? '1px solid var(--brand-purple)' : '1px solid var(--border-default)',
                background: statusFilter === f ? 'rgba(246, 116, 37, 0.1)' : 'transparent',
                color: statusFilter === f ? 'var(--brand-purple)' : 'var(--text-muted)',
                cursor: 'pointer', fontSize: '0.8125rem', fontWeight: 500,
                textTransform: 'capitalize', transition: 'all 0.2s ease'
              }}
            >
              {f} ({f === 'all' ? programs.length : programs.filter(p => p.status === f.toUpperCase()).length})
            </button>
          ))}
        </div>
        <button onClick={seedPrograms} disabled={actionLoading} className="btn btn-secondary btn-sm" data-testid="seed-btn">
          <Database size={16} />
          {actionLoading ? 'Seeding...' : 'Seed Programs'}
        </button>
      </div>

      <div style={{ fontSize: '0.8125rem', color: 'var(--text-muted)', marginBottom: '1rem' }}>
        {programStats?.countries || 0} countries, {programStats?.total_programs || 0} programs, {programStats?.total_rules || 0} rules
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
        {filteredPrograms.map(program => (
          <ProgramCard
            key={program.program_id}
            program={program}
            expanded={expandedProgram === program.program_id}
            details={programDetails[program.program_id]}
            onToggle={toggleProgram}
            onVerify={verifyProgram}
            actionLoading={actionLoading}
            onAddRule={setShowAddRule}
            onAddSource={setShowAddSource}
            onDeleteRule={deleteRule}
          />
        ))}
        {filteredPrograms.length === 0 && (
          <div style={{ textAlign: 'center', padding: '3rem', color: 'var(--text-muted)' }}>
            No programs found. <button onClick={seedPrograms} style={{ color: 'var(--brand-purple)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Seed database</button>
          </div>
        )}
      </div>

      {showAddRule && (
        <RuleModal programId={showAddRule} onSubmit={addRule} onClose={() => setShowAddRule(null)} actionLoading={actionLoading} />
      )}
      {showAddSource && (
        <SourceModal programId={showAddSource} onSubmit={addSource} onClose={() => setShowAddSource(null)} actionLoading={actionLoading} />
      )}
    </div>
  );
};
